import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

// Supported languages for the public-facing web app.
export const LANGUAGES = [
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
];

export const SUPPORTED_CODES = LANGUAGES.map((l) => l.code);
const STORAGE_KEY = 'nautiqo_lang';
const DEFAULT_LANG = 'it';

function detectInitialLang() {
  try {
    // 1. Explicit URL override (?lang=es) — useful for ad campaigns per market
    const params = new URLSearchParams(window.location.search);
    const urlLang = (params.get('lang') || '').toLowerCase();
    if (SUPPORTED_CODES.includes(urlLang)) return urlLang;

    // 2. Previously saved preference
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && SUPPORTED_CODES.includes(saved)) return saved;

    // 3. Browser language
    const nav = (navigator.language || navigator.userLanguage || '').slice(0, 2).toLowerCase();
    if (SUPPORTED_CODES.includes(nav)) return nav;
  } catch {
    /* ignore */
  }
  return DEFAULT_LANG;
}

const LanguageContext = createContext({
  lang: DEFAULT_LANG,
  setLang: () => {},
  languages: LANGUAGES,
});

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(detectInitialLang);

  const setLang = useCallback((code) => {
    if (!SUPPORTED_CODES.includes(code)) return;
    setLangState(code);
    try {
      localStorage.setItem(STORAGE_KEY, code);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    try {
      document.documentElement.lang = lang;
    } catch {
      /* ignore */
    }
  }, [lang]);

  return (
    <LanguageContext.Provider value={{ lang, setLang, languages: LANGUAGES }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  return useContext(LanguageContext);
}

/**
 * Convenience hook for a page that holds its own translations object.
 * Usage:
 *   const t = usePageT(translations); // returns translations[currentLang] with IT fallback
 */
export function usePageT(translations) {
  const { lang } = useLanguage();
  return translations[lang] || translations[DEFAULT_LANG] || {};
}
